import React, { useContext } from "react";
import { useHistory } from "react-router-dom";
import Icon from "../ui/Icon";
import IconButton from "../ui/IconButton";
import { UserContext } from "../../contexts";
import { useNotificationSummary } from "../../notifications/useNotificationCenter";
import { COMMAND_PALETTE_EVENT } from "../ui/SearchInput";
import ThemeSwitch from "./ThemeSwitch";
import { usePageChrome } from "./pageChrome.jsx";
import "./AppMobileTopbar.scss";

// Fontaine M29 — phone top row (56px, sticky): search · bell · profile.
//
// The page IS named here, and only here. It used to be named twice — this bar
// and, one row below, a PageHeader whose <h1> said the same word at twice the
// size beside a back arrow. On a phone that block cost roughly a third of the
// screen, so below 900px PageHeader drops its title row and publishes the name
// up to this bar instead (see pageChrome.jsx).
//
// The five actions stay: at 390px they take 240px of 390, which leaves the
// title a narrow column, so it truncates with an ellipsis rather than wrapping
// or pushing them off the end. A short page name shows in full; a long one is
// cut, and the tabs immediately below still say where you are.

export default function AppMobileTopbar() {
  const chrome = usePageChrome();
  const history = useHistory();
  const { user } = useContext(UserContext) ?? {};
  const { data: summary } = useNotificationSummary(!!user);
  const unread = summary?.unread_count ?? 0;

  const goBack = () => {
    if (chrome?.onBack) return chrome.onBack();
    if (chrome?.backTo) return history.push(chrome.backTo);
    return history.goBack();
  };

  return (
    <header className="ft-mtop" role="banner">
      {/* The page, named once. Truncates rather than wrapping — the bar is one
          line and a long section name must not push the actions off it. */}
      <div className="ft-mtop__page">
        {chrome?.showBackButton !== false && chrome ? (
          <IconButton label="Back" variant="ghost" size="md" className="ft-mtop__back" onClick={goBack}>
            <Icon name="ArrowLeft" size="md" />
          </IconButton>
        ) : null}
        {chrome?.title != null ? <h1 className="ft-mtop__title">{chrome.title}</h1> : null}
      </div>
      <div className="ft-mtop__actions">
        <ThemeSwitch compact size="sm" />
        <IconButton label="Search" variant="ghost" size="lg" onClick={() => window.dispatchEvent(new CustomEvent(COMMAND_PALETTE_EVENT))}><Icon name="Search" size="md" /></IconButton>
        <IconButton label="Messages" variant="ghost" size="lg" onClick={() => history.push("/messages")}><Icon name="MessageCircle" size="md" /></IconButton>
        <span className="ft-mtop__bell">
          <IconButton label={unread ? `Notifications, ${unread} unread` : "Notifications"} variant="ghost" size="lg" onClick={() => history.push("/notifications")}><Icon name="Bell" size="md" /></IconButton>
          {/* A dot, not a count. "99+" said nothing an unread dot does not —
              nobody acts differently on 99 than on 12 — and at that width it was
              the loudest thing in the bar (Alex, 2026-09-25). The number is still
              in the button's accessible name, so it is not lost, just not shouted.
              `aria-hidden` because that label already carries it. */}
          {unread ? <span className="ft-mtop__dot" aria-hidden="true" /> : null}
        </span>
        {/* No avatar here either: Profile is an item in the More sheet now,
            directly above Settings. */}
      </div>
    </header>
  );
}
